/**
 * Crew-naam uit een draaidag → persoon uit de crew-pool (`/Api/personen/crew`).
 *
 * Volgorde: exacte volledige naam → unieke voornaam → "bevat" (alleen als niemand die voornaam heeft).
 * Bij meerdere kandidaten met dezelfde voornaam (prod okt 2026: Robin van Engen = Medewerker,
 * Robin Swaanen + Robin Zom = Freelancer) geldt het besluit van Joris (21 okt 2026):
 *  - een kale voornaam krijgt de enige vaste Medewerker (Type = Medewerker);
 *  - staat de naam in de draaidagtitel als extern gemarkeerd ("(b)" bevestigd / "(g)" gevraagd),
 *    dan géén medewerker-voorrang: de enige freelancer/leverancier, anders handmatig;
 *  - twee of meer medewerkers met die voornaam → handmatig.
 * Een match via voorrang krijgt een `reden`, zodat de wizard dat subtiel kan tonen en de PL kan corrigeren.
 */

export const fullName = (p) => p?.fields?.['Volledige naam'] || p?.fields?.Naam || '';
const norm = (s) => String(s || '').trim().toLowerCase();
const voornaam = (s) => norm(s).split(/\s+/)[0] || '';

export const isMedewerker = (p) => !p?._source && p?.fields?.Type === 'Medewerker';
export const isExtern = (p) => p?._source === 'leverancier' || p?.fields?.Type === 'Freelancer';

export const REDEN_MEDEWERKER = 'gekozen: enige medewerker met deze naam';
export const REDEN_EXTERN = 'gekozen: enige freelancer met deze naam (extern in titel)';

/**
 * @param {string} naam  naam zoals in de draaidag (vaak alleen een voornaam)
 * @param {object[]} pool  personen-records ({ id, fields: { 'Volledige naam', Type }, _source? })
 * @param {{ extern?: boolean }} opties  extern = in de titel gemarkeerd met (b) of (g)
 * @returns {{ person: object, reden: string|null } | null}
 */
export function matchCrewPersoon(naam, pool, { extern = false } = {}) {
  const n = norm(naam);
  const lijst = Array.isArray(pool) ? pool : [];
  if (!n) return null;
  const exact = lijst.find((p) => norm(fullName(p)) === n);
  if (exact) return { person: exact, reden: null };
  const eerste = voornaam(n);
  const kandidaten = lijst.filter((p) => voornaam(fullName(p)) === eerste);
  if (kandidaten.length === 1) return { person: kandidaten[0], reden: null };
  if (kandidaten.length === 0) {
    const bevat = lijst.find((p) => norm(fullName(p)).includes(n));
    return bevat ? { person: bevat, reden: null } : null;
  }
  // Meerdere kandidaten. Voorrang alleen voor een kale voornaam: bij "Robin Z" weten we niet welke Robin.
  if (/\s/.test(n)) return null;
  const groep = kandidaten.filter(extern ? isExtern : isMedewerker);
  if (groep.length !== 1) return null;
  return { person: groep[0], reden: extern ? REDEN_EXTERN : REDEN_MEDEWERKER };
}

/**
 * Titelnamen (lowercase, precies zoals in de titel) die als extern gemarkeerd zijn — (b) of (g).
 * Bewust per naam en niet per voornaam: in "Robin + Robin Zom (b)" is de kale "Robin" níét gemarkeerd.
 * @param {{naam:string,status:string|null}[]} titelCrew  uitvoer van crewUitTitel()
 */
export function externGemarkeerd(titelCrew) {
  return new Set((titelCrew || [])
    .filter((x) => x && (x.status === 'bevestigd' || x.status === 'gevraagd'))
    .map((x) => norm(x.naam).replace(/\s+/g, ' '))
    .filter(Boolean));
}

export const isExternGemarkeerd = (naam, set) => !!set && set.has(norm(naam).replace(/\s+/g, ' '));
